// Settings › Dispatch › Channel cut-offs & dashboard metrics (Alex,
// 2026-10-13). Mirrors app/warehouse/controllers/dispatchChannelRules.controller.js
// and services/dispatch-rules.js: per warehouse, per sales channel, a
// weekday grid of order cut-off + ship-by day (standard / express), the
// "on dashboard metrics" tick and the warehouse ship cut-off — versioned
// by the date each save takes effect, so history never moves.
import { apiFetch } from "./apiFetch";

export type RuleService = "standard" | "express";
export const RULE_SERVICES: RuleService[] = ["standard", "express"];
/** ISO weekdays as the grid keys them: 1 = Monday … 7 = Sunday. */
export type Weekday = "1" | "2" | "3" | "4" | "5" | "6" | "7";
export const WEEKDAYS: Weekday[] = ["1", "2", "3", "4", "5", "6", "7"];

export interface GridCell {
    /** "HH:MM" London; null = no cut-off that day (the whole day is that day). */
    cutoff: string | null;
    /** Calendar days after day 0 the order must ship by (0 = same day). */
    ship_days: number;
}
export type RuleGrid = Record<RuleService, Record<Weekday, GridCell>>;

export interface RuleVersion {
    id: number | null;
    channel_key: string;
    /** YYYY-MM-DD the version applies from; null for the built-in default. */
    effective_from: string | null;
    include_in_metrics: boolean;
    /** null on a channel row = inherits the warehouse default's grid. */
    grid: RuleGrid | null;
    /** "HH:MM"; warehouse default only. */
    ship_cutoff: string | null;
    created_at: string | null;
    created_by_name: string | null;
    builtin?: boolean;
}

export interface ChannelRule {
    channel_key: string;
    /** The spelling the orders use most ("Jobalots UK" over "jobalots uk"). */
    display_name: string;
    /** Orders seen in the last 90 days. */
    orders: number;
    last_order_at: string | null;
    /** The version in force today, or null = nothing saved (inherits the default, on the metrics). */
    current: RuleVersion | null;
    versions: RuleVersion[];
}

export interface ChannelRulesPayload {
    warehouse: string;
    today: string;
    default: RuleVersion & { versions: RuleVersion[] };
    channels: ChannelRule[];
}

export interface SaveRuleInput {
    warehouse: string;
    channel_key: string;
    include_in_metrics: boolean;
    grid: RuleGrid | null;
    ship_cutoff?: string | null;
}

export interface TodayCutoffs {
    warehouse: string;
    today: string;
    /** Today's standard order cut-off, "HH:MM" London; null = none today. */
    order_cutoff: string | null;
    order_cutoff_express: string | null;
    /** The next day's cut-off when today has none. */
    next_order_cutoff: string;
    ship_cutoff: string;
    /**
     * Settings › Picking › "Must ship today" always on (2026-11-04): today
     * is one of the weekdays on which the clock toggle shows all day, so
     * the screens treat it as past the cut-off from midnight.
     */
    due_filter_always?: boolean;
}

/** The built-in grid (= the rule before this feature). Mirrors DEFAULT_GRID on the server. */
export const DEFAULT_GRID: RuleGrid = {
    standard: {
        "1": { cutoff: "13:00", ship_days: 1 }, "2": { cutoff: "13:00", ship_days: 0 }, "3": { cutoff: "13:00", ship_days: 0 },
        "4": { cutoff: "13:00", ship_days: 0 }, "5": { cutoff: "13:00", ship_days: 0 }, "6": { cutoff: null, ship_days: 3 },
        "7": { cutoff: null, ship_days: 2 },
    },
    express: {
        "1": { cutoff: "13:00", ship_days: 0 }, "2": { cutoff: "13:00", ship_days: 0 }, "3": { cutoff: "13:00", ship_days: 0 },
        "4": { cutoff: "13:00", ship_days: 0 }, "5": { cutoff: "13:00", ship_days: 0 }, "6": { cutoff: null, ship_days: 2 },
        "7": { cutoff: null, ship_days: 1 },
    },
};
export const DEFAULT_SHIP_CUTOFF = "15:30";

export class ChannelRulesError extends Error {
    status: number;
    constructor(status: number, message: string) {
        super(message);
        this.status = status;
    }
}

const readError = async (res: Response): Promise<never> => {
    let message = `HTTP ${res.status}`;
    try {
        const body = await res.json();
        if (body && typeof body.message === "string") message = body.message;
    } catch { /* keep the status text */ }
    throw new ChannelRulesError(res.status, message);
};

export const fetchChannelRules = async (warehouse: string): Promise<ChannelRulesPayload> => {
    const res = await apiFetch(`/dispatch-admin/channel-rules?warehouse=${encodeURIComponent(warehouse)}`, {});
    if (!res.ok) return readError(res);
    return res.json();
};

export const saveChannelRule = async (input: SaveRuleInput): Promise<{ version: RuleVersion; rules: ChannelRulesPayload }> => {
    const res = await apiFetch(`/dispatch-admin/channel-rules`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(input),
    });
    if (!res.ok) return readError(res);
    return res.json();
};

/** Today's cut-offs for the caller's warehouse (or the one given). */
export const fetchTodayCutoffs = async (warehouse?: string | null): Promise<TodayCutoffs> => {
    const qs = warehouse ? `?warehouse=${encodeURIComponent(warehouse)}` : "";
    const res = await apiFetch(`/dispatch-admin/channel-rules/today${qs}`, {});
    if (!res.ok) return readError(res);
    return res.json();
};

// ── Grid helpers shared by the editor ───────────────────────────────
const DAY_LABELS: Record<Weekday, string> = { "1": "Mon", "2": "Tue", "3": "Wed", "4": "Thu", "5": "Fri", "6": "Sat", "7": "Sun" };
export const weekdayLabel = (d: Weekday): string => DAY_LABELS[d];
/** The weekday `ship_days` after `d` ("Tue" for Sat + 3). */
export const shipDayLabel = (d: Weekday, shipDays: number): string =>
    DAY_LABELS[String(((Number(d) - 1 + shipDays) % 7) + 1) as Weekday];

const TIME_RE = /^([01]\d|2[0-3]):([0-5]\d)$/;
export const isValidTime = (v: string): boolean => TIME_RE.test(v);

/**
 * Mirrors the server's validateGrid: every cell a time (not 00:00) or
 * empty, ship_days 0–6, and no weekday shipping earlier than the day
 * before it (ship_days[next] >= ship_days[d] - 1, cyclic). Returns the
 * first problem as a message key + params, or null when valid.
 */
export const gridProblem = (grid: RuleGrid): { key: string; params?: Record<string, string> } | null => {
    for (const svc of RULE_SERVICES) {
        for (const d of WEEKDAYS) {
            const c = grid[svc][d];
            if (c.cutoff !== null && c.cutoff !== "" && !isValidTime(c.cutoff)) return { key: "channel_rules_bad_time", params: { day: DAY_LABELS[d] } };
            if (c.cutoff === "00:00") return { key: "channel_rules_midnight_cutoff", params: { day: DAY_LABELS[d] } };
            if (!Number.isInteger(c.ship_days) || c.ship_days < 0 || c.ship_days > 6) return { key: "channel_rules_bad_ship_days", params: { day: DAY_LABELS[d] } };
        }
        for (let i = 0; i < WEEKDAYS.length; i++) {
            const d = WEEKDAYS[i];
            const next = WEEKDAYS[(i + 1) % WEEKDAYS.length];
            if (grid[svc][next].ship_days < grid[svc][d].ship_days - 1) {
                return { key: "channel_rules_not_monotone", params: { day: DAY_LABELS[d], next: DAY_LABELS[next] } };
            }
        }
    }
    return null;
};

export const cloneGrid = (grid: RuleGrid): RuleGrid => ({
    standard: { ...Object.fromEntries(WEEKDAYS.map((d) => [d, { ...grid.standard[d] }])) } as Record<Weekday, GridCell>,
    express: { ...Object.fromEntries(WEEKDAYS.map((d) => [d, { ...grid.express[d] }])) } as Record<Weekday, GridCell>,
});
